import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MagnifyingGlass, ShoppingCart, User, List, X, Phone, Lightning, FileText, Package } from "@phosphor-icons/react";
import { api, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const NAV_CATEGORIES = [
  { id: "ella", name: "Eļļa" },
  { id: "auto-spuldzes", name: "Auto spuldzes" },
  { id: "autokimija", name: "Autoķīmija" },
  { id: "antifrizs-dzesesanas-skidrumi", name: "Antifrīzs" },
  { id: "motip-krasas-aerosoli-akrils", name: "MOTIP krāsas" },
  { id: "logu-slotinas", name: "Logu slotiņas" },
  { id: "autokosmetika", name: "Autokosmētika" },
  { id: "auto-garazai-servisiem-preces", name: "Servisiem" },
];

export default function Header() {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { count } = useCart();
  const { user } = useAuth();
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products/suggest", { params: { q } });
        setSuggestions(data);
        setOpen(true);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    setOpen(false);
    if (q.trim()) navigate(`/katalogs?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-border">
      <div className="hidden md:block bg-[#2E86D1] text-white text-xs">
        <div className="mx-auto max-w-7xl px-4 flex items-center justify-between h-8">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><Phone size={13} /> +371 67 000 000</span>
            <span>info@e-dansg.lv</span>
          </div>
          <div className="flex items-center gap-5 font-semibold tracking-wide uppercase text-[11px]">
            <Link to="/blogs" data-testid="topbar-blog-link" className="hover:text-blue-100">Blogs</Link>
            <Link to="/par-mums" data-testid="topbar-about-link" className="hover:text-blue-100">Par mums</Link>
            <Link to="/b2b" data-testid="topbar-quote-link" className="hover:text-blue-100">B2B sadarbība</Link>
            <Link to="/kontakti" data-testid="topbar-contacts-link" className="rounded-full bg-white px-3 py-1 text-[#2E86D1] hover:bg-blue-50">Kontakti</Link>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 flex items-center gap-4 h-16 md:h-20">
        <button data-testid="mobile-menu-btn" aria-label="Izvēlne" className="lg:hidden p-1" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X size={26} /> : <List size={26} />}
        </button>

        <Link to="/" data-testid="header-logo" className="shrink-0">
          <img src="https://customer-assets-lxgj4vgw.emergentagent.net/job_industrial-parts-pro-5/artifacts/eto47ty6_logo.png" alt="DANS G" className="h-8 md:h-10 w-auto" />
        </Link>

        <div ref={boxRef} className="relative flex-1 min-w-0 max-w-2xl mx-auto">
          <form onSubmit={submitSearch} className="flex items-center overflow-hidden rounded-full border border-slate-300 bg-white pr-1 focus-within:border-[#2E86D1] focus-within:ring-2 focus-within:ring-blue-200">
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => q.length >= 2 && setOpen(true)}
              placeholder="Meklēt pēc nosaukuma, artikula vai zīmola..."
              className="h-10 md:h-11 w-full min-w-0 flex-1 bg-transparent px-4 md:px-5 text-sm focus:outline-none"
            />
            <button data-testid="header-search-btn" aria-label="Meklēt" type="submit" className="btn-industrial flex h-8 md:h-9 w-11 shrink-0 items-center justify-center rounded-full bg-[#2E86D1] text-white hover:bg-[#1F6DB2]">
              <MagnifyingGlass size={18} />
            </button>
          </form>
          {open && suggestions.length > 0 && (
            <div data-testid="search-suggestions" className="absolute top-full left-0 right-0 mt-2 max-h-96 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-xl z-50">
              {suggestions.map((s) => (
                <Link
                  key={s.sku}
                  to={`/produkts/${s.slug}`}
                  data-testid={`suggestion-${s.sku}`}
                  onClick={() => { setOpen(false); setQ(""); }}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-[#F4F5F7] border-b border-border last:border-b-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.name}</p>
                    <p className="text-xs text-slate-500">{s.sku}</p>
                  </div>
                  <span className="text-sm font-bold whitespace-nowrap">{s.price_tiers?.[0] ? formatPrice(s.price_tiers[0].price) : ""}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 md:gap-3 shrink-0">
          <Link to="/atra-pasutisana" data-testid="header-quickorder-link" className="hidden md:flex items-center gap-1.5 text-xs font-semibold tracking-wide hover:text-[#2E86D1] px-2">
            <Lightning size={18} weight="fill" className="text-[#2E86D1]" /> Ātrā pasūtīšana
          </Link>
          <Link to={user ? "/konts" : "/pieteikties"} data-testid="header-account-link" aria-label="Konts" className="p-2 hover:text-[#2E86D1]">
            <User size={24} />
          </Link>
          <Link to="/grozs" data-testid="header-cart-link" aria-label="Grozs" className="relative p-2 hover:text-[#2E86D1]">
            <ShoppingCart size={24} />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-[#2E86D1] text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center px-1">
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>

      <nav className="hidden lg:block border-t border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 flex items-center h-11 gap-1">
          {NAV_CATEGORIES.map((c) => (
            <Link key={c.id} to={`/katalogs?category=${c.id}`} data-testid={`nav-cat-${c.id}`}
              className="btn-industrial px-3 h-full flex items-center whitespace-nowrap text-[13px] font-semibold uppercase tracking-wide hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]">
              {c.name}
            </Link>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Link to="/pasutijuma-statuss" data-testid="nav-track-link" className="btn-industrial flex items-center gap-1.5 px-3 h-8 whitespace-nowrap text-[13px] font-semibold border border-slate-300 hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]">
              <Package size={16} /> Pasūtījuma statuss
            </Link>
            <Link to="/piedavajums" data-testid="nav-quote-link" className="btn-industrial flex items-center gap-1.5 px-3 h-8 whitespace-nowrap text-[13px] font-semibold border border-slate-300 hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]">
              <FileText size={16} /> Pieprasīt piedāvājumu
            </Link>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <nav data-testid="mobile-menu" className="lg:hidden border-t border-border bg-white max-h-[70vh] overflow-auto">
          {NAV_CATEGORIES.map((c) => (
            <Link key={c.id} to={`/katalogs?category=${c.id}`} onClick={() => setMobileOpen(false)}
              className="block px-4 py-3 border-b border-border text-sm font-semibold uppercase">
              {c.name}
            </Link>
          ))}
          <Link to="/atra-pasutisana" onClick={() => setMobileOpen(false)} className="block px-4 py-3 border-b border-border text-sm font-semibold text-[#2E86D1]">
            Ātrā pasūtīšana
          </Link>
          <Link to="/pasutijuma-statuss" onClick={() => setMobileOpen(false)} className="block px-4 py-3 border-b border-border text-sm font-semibold">
            Pasūtījuma statuss
          </Link>
          <Link to="/piedavajums" onClick={() => setMobileOpen(false)} className="block px-4 py-3 border-b border-border text-sm font-semibold">
            Pieprasīt piedāvājumu
          </Link>
          <Link to="/b2b" onClick={() => setMobileOpen(false)} className="block px-4 py-3 border-b border-border text-sm font-semibold">
            B2B sadarbība
          </Link>
          <Link to="/blogs" onClick={() => setMobileOpen(false)} className="block px-4 py-3 border-b border-border text-sm font-semibold">
            Blogs
          </Link>
          <Link to="/par-mums" onClick={() => setMobileOpen(false)} className="block px-4 py-3 border-b border-border text-sm font-semibold">
            Par mums
          </Link>
          <Link to="/kontakti" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm font-semibold text-[#2E86D1]">
            Kontakti
          </Link>
        </nav>
      )}
    </header>
  );
}
